import React, { useState } from 'react';
import Head from 'next/head';
import { Omega } from 'lucide-react';
import NavBar from '../components/layouts/NavBar';
import Footer from '../components/layouts/Footer';
import ToolHeader from '../components/common/ToolHeader';
import SymbolCard from '../components/SymbolCard';
import { symbolCategories, supportedLanguages, glyph, type SymbolEntry } from '@/lib/symbols';

export default function SymbolsPage() {
    const [activeCategory, setActiveCategory] = useState('all');
    const [activeLanguage, setActiveLanguage] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const isSearching = searchQuery.length > 0;

    let filteredContent: SymbolEntry[] = [];

    if (isSearching) {
        const query = searchQuery.toLowerCase();
        const allSymbols = symbolCategories.flatMap((cat) => cat.symbols);
        filteredContent = allSymbols.filter(
            (s) => s.name.toLowerCase().includes(query) || glyph(s.code).includes(query),
        );
    } else if (activeCategory !== 'all') {
        filteredContent = symbolCategories.find((cat) => cat.id === activeCategory)?.symbols || [];

        if (activeCategory === 'language' && activeLanguage !== 'all') {
            filteredContent = filteredContent.filter((s) => s.languages?.includes(activeLanguage));
        }
    }

    const pill = (active: boolean) =>
        `shrink-0 cursor-pointer whitespace-nowrap rounded-full border-none px-3 py-1.5 text-[13px] font-medium transition-colors ${
            active ? 'bg-purple text-white hover:bg-purple' : 'bg-subtle text-muted hover:bg-line'
        }`;

    const grid = 'grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-1.5';

    return (
        <div className="flex min-h-screen flex-col">
            <Head>
                <title>Symbols - Maths Tools</title>
            </Head>

            <NavBar />

            <main className="flex-1">
                <ToolHeader
                    icon={<Omega size={20} />}
                    iconColor="amber"
                    title="Symbols"
                    description="Click any symbol to copy it to your clipboard. Organised by category for quick access."
                    category="reference"
                />

                {/* Filter + language bars as one sticky unit — no hardcoded offsets,
                    so it can't overlap when the filter bar wraps on small screens. */}
                <div className="sticky top-13 z-10">
                    <div className="flex flex-col gap-3 border-b-[0.5px] border-line bg-surface px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6">
                        <div className="flex flex-wrap gap-1.5 py-1">
                            <button
                                className={pill(activeCategory === 'all' && !isSearching)}
                                onClick={() => {
                                    setActiveCategory('all');
                                    setActiveLanguage('all');
                                    setSearchQuery('');
                                }}
                            >
                                All
                            </button>
                            {symbolCategories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={pill(activeCategory === cat.id && !isSearching)}
                                    onClick={() => {
                                        setActiveCategory(cat.id);
                                        setActiveLanguage('all');
                                        setSearchQuery('');
                                    }}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                        <div className="w-full sm:max-w-60">
                            <input
                                type="text"
                                placeholder="Search symbols..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                aria-label="Search symbols"
                            />
                        </div>
                    </div>

                    {activeCategory === 'language' && !isSearching && (
                        <div className="flex items-center gap-2.5 border-b-[0.5px] border-line bg-subtle px-4 py-2 sm:px-6">
                            <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wider text-hint">
                                Language
                            </span>
                            <div className="flex flex-wrap gap-1.5 py-0.5">
                                <button
                                    className={pill(activeLanguage === 'all')}
                                    onClick={() => setActiveLanguage('all')}
                                >
                                    All
                                </button>
                                {supportedLanguages.map((language) => (
                                    <button
                                        key={language}
                                        className={pill(activeLanguage === language)}
                                        onClick={() => setActiveLanguage(language)}
                                    >
                                        {language}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mx-auto w-full max-w-300 p-4 sm:p-6">
                    {isSearching ? (
                        filteredContent.length > 0 ? (
                            <div className={grid}>
                                {filteredContent.map((s, idx) => (
                                    <SymbolCard key={idx} name={s.name} code={s.code} />
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 text-center text-sm text-hint">
                                No symbols match your search.
                            </div>
                        )
                    ) : activeCategory === 'all' ? (
                        <div className="flex flex-col gap-8">
                            {symbolCategories.map((cat) => (
                                <section key={cat.id}>
                                    <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-hint">
                                        {cat.label}
                                    </h2>
                                    <div className={grid}>
                                        {cat.symbols.map((s, idx) => (
                                            <SymbolCard key={idx} name={s.name} code={s.code} />
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    ) : (
                        <div className={grid}>
                            {filteredContent.map((s, idx) => (
                                <SymbolCard key={idx} name={s.name} code={s.code} />
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
